import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { apiConstants, postGeneric } from "../../common/constants";
import { ACCENTS, CARD_SX, COLORS, LIFT, fmtCount, fmtPct, pct, rise } from "./emsTheme";

/** The drill-down flags this strip sets on the list (see EscalationService.buildSearchCriteria). */
export const STRIP_FLAGS = ["overdueOnly", "dueTodayOnly", "unassignedOnly"];

const TILES = [
    { key: null, label: "Open", icon: Inventory2OutlinedIcon, accent: "purple", field: "unresolvedCount",
        sub: () => "open and pending", info: "Open or pending tickets for these filters" },
    { key: "overdueOnly", label: "Past TAT", icon: AlarmOnIcon, accent: "red", field: "overdueCount",
        sub: (v, s) => (s.unresolvedCount ? `${fmtPct(pct(v, s.unresolvedCount), 0)} of open` : "of open"), info: "Open tickets already past their resolution TAT" },
    { key: "dueTodayOnly", label: "Due today", icon: ScheduleIcon, accent: "orange", field: "dueTodayCount",
        sub: (v) => (v ? "TAT ends today" : "nothing due today"), info: "Open tickets whose resolution TAT ends today" },
    { key: "unassignedOnly", label: "Unassigned", icon: PersonOffOutlinedIcon, accent: "blue", field: "unassignedCount",
        sub: (v) => (v ? "open with no owner" : "every ticket has an owner"), info: "Open tickets with nobody assigned" },
];

/**
 * Four headline counts over the same filters as the list (one stats request, debounced), each
 * a one-click drill-down into the list. The strip's own flags are left out of its request, so
 * the numbers stay put while one of them is applied; the applied one is highlighted and a second
 * click clears it.
 */
const TicketsSummaryStrip = ({ filters, activeKey, openTabActive, onSelect, reloadTick }) => {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    const requestKey = useMemo(() => {
        const f = { ...(filters || {}) };
        STRIP_FLAGS.forEach((k) => delete f[k]);
        return JSON.stringify(f);
    }, [filters]);

    // The debounce is for filter changes; the first load goes out straight away.
    const firstRunRef = useRef(true);
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const delay = firstRunRef.current ? 0 : 350;
        firstRunRef.current = false;
        const t = setTimeout(async () => {
            try {
                // Four counts only, so ask for the summary alone (no breakdowns, trend or 14-day series).
                const response = await postGeneric(`${apiConstants.ESCALATION_STATS_V2}?summaryOnly=true`, JSON.parse(requestKey));
                if (cancelled) return;
                setSummary(response?.data?.summary || {});
                setFailed(false);
            } catch (e) {
                if (!cancelled) { setFailed(true); setSummary(null); }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, delay);
        return () => { cancelled = true; clearTimeout(t); };
    }, [requestKey, reloadTick]);

    return (
        <Box component="section" aria-label="Ticket summary"
            sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: { xs: 1.25, md: 2 }, mb: 2 }}>
            {TILES.map((tile, i) => {
                const a = ACCENTS[tile.accent];
                const value = summary ? summary[tile.field] ?? 0 : null;
                const active = tile.key ? activeKey === tile.key : openTabActive && !activeKey;
                const Icon = tile.icon;
                const allClear = tile.key && value === 0;
                const label = `${tile.label}: ${value == null ? "loading" : fmtCount(value)}. ${active && tile.key ? "Applied, click to clear" : "Show these tickets"}`;
                return (
                    <Box key={tile.label} component="button" type="button" onClick={() => onSelect(tile.key, active)} title={tile.info}
                        aria-pressed={active} aria-label={label}
                        sx={{ all: "unset", boxSizing: "border-box", cursor: "pointer", ...CARD_SX, p: { xs: 1.5, md: 2 }, pt: { xs: 1.75, md: 2.25 }, position: "relative", overflow: "hidden",
                            minWidth: 0, display: "flex", gap: 1.5, alignItems: "flex-start", ...LIFT, ...rise(i + 1),
                            borderColor: active ? a.solid : COLORS.grid, bgcolor: active ? a.soft : COLORS.surface,
                            "&::before": { content: '""', position: "absolute", left: 0, right: 0, top: 0, height: 3, background: a.grad },
                            "&:focus-visible": { outline: `2px solid ${a.solid}`, outlineOffset: 2 } }}>
                        <Box aria-hidden sx={{ width: 36, height: 36, borderRadius: "10px", background: a.grad, display: { xs: "none", sm: "grid" }, placeItems: "center", flexShrink: 0,
                            boxShadow: `0 4px 10px ${a.solid}33` }}>
                            <Icon sx={{ fontSize: 19, color: "#fff" }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: a.solid, whiteSpace: "nowrap" }}>{tile.label}</Typography>
                                {active && tile.key && <CheckCircleIcon sx={{ fontSize: 15, color: a.solid }} aria-hidden />}
                            </Box>
                            {loading && !summary ? <Skeleton width={56} height={34} /> : (
                                <Typography sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, color: COLORS.ink, lineHeight: 1.25, fontVariantNumeric: "tabular-nums",
                                    opacity: loading ? 0.6 : 1, transition: "opacity 200ms" }}>
                                    {failed ? "—" : fmtCount(value)}
                                </Typography>
                            )}
                            <Typography sx={{ fontSize: 12, color: active && tile.key ? a.solid : allClear ? ACCENTS.green.solid : COLORS.ink3, fontWeight: active && tile.key ? 700 : 500,
                                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {failed ? "Couldn't load" : active && tile.key ? "Applied · click to clear" : summary ? tile.sub(value, summary) : " "}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default TicketsSummaryStrip;
